import { router } from '@/Router';
import isDeepEqual from 'fast-deep-equal/es6';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { z } from 'zod';
import { createWithEqualityFn } from 'zustand/traditional';
import type { FilterIds, FilterRouteConfig } from './FilterBar';
import type { filterSchema } from './registry';
import {
  createStorageKey,
  getFilterData,
  hasFilterData,
  isFilterExpired,
  loadFilterConfig,
  removeFilterData,
  saveFilterConfig,
  saveFilterData,
  updateFilterTimestamp,
} from './storage.utils';
import { cleanEmptyParams } from './utils';

/**
 * Here we create a store to manage the filter state globally in the app and save it to local storage
 * By saving to local storage, we can restore the state when the user comes back to the page
 * This is useful for the filter bar, where we want to keep the state even if the user navigates away and comes back later
 * We also want to make sure we track the last updated timestamp for each filter, then we can clean up expired filters
 */

const __PREFIX__ = 'zn/filterbar:';

type SetItemCb<T extends z.ZodType<unknown, z.ZodTypeDef>> =
  | ((prev: Partial<z.infer<T>>) => Partial<z.infer<T>>)
  | Partial<z.infer<T>>
  | undefined;

type FilterStore<Schema extends z.ZodType<unknown, z.ZodTypeDef> = typeof filterSchema> = {
  setItem: (routeId: FilterIds['routeId'], value: SetItemCb<Schema> | undefined) => void;
  getItem: (routeId: FilterIds['routeId']) => Partial<z.infer<Schema>>;
  initRoute: (routeId: FilterIds['routeId'], saveToLocalStorage: boolean) => void;

  saveToLocalStorage: boolean;
  [k: `${typeof __PREFIX__}${string}`]: unknown | undefined;
};

const filterStore = createWithEqualityFn<FilterStore>(
  (set, get) => ({
    saveToLocalStorage: false,
    setItem(routeId, cb) {
      const key = createStorageKey(routeId);
      const value = cb instanceof Function ? cb(get()?.[key] || {}) : cb;

      if (get().saveToLocalStorage === false) {
        set({ [key]: value ?? {} });
        return;
      }

      if (!value) {
        if (hasFilterData(key)) {
          removeFilterData(key);
        }
        set({ [key]: {} });
        return;
      }

      updateFilterTimestamp(routeId);
      saveFilterData(key, value);
      set({ [key]: value });
    },

    getItem(routeId) {
      const key = createStorageKey(routeId);
      const _store = get();

      if (_store.saveToLocalStorage === false) {
        const state = _store?.[key];

        if (!state || typeof state !== 'object') {
          set({ [key]: {} });
        }

        return state ?? {};
      }

      // Check if this filter has expired
      const config = loadFilterConfig();
      if (isFilterExpired(config.lastUpdated[routeId])) {
        removeFilterData(key);
        delete config.lastUpdated[routeId];
        saveFilterConfig(config);
        set({ [key]: {} });
        return {};
      }

      const value = getFilterData(key);
      set({ [key]: value });

      return value as Partial<z.infer<typeof filterSchema>>;
    },

    initRoute(routeId, saveToLocalStorage) {
      const key = createStorageKey(routeId);
      set({ saveToLocalStorage });

      if (saveToLocalStorage === false) {
        set({ [key]: {} });

        // cleanup in case localStorage was used before
        if (hasFilterData(key)) {
          removeFilterData(key);
        }

        return;
      }

      // Check if this filter has expired before initializing
      const config = loadFilterConfig();
      if (isFilterExpired(config.lastUpdated[routeId])) {
        removeFilterData(key);
        delete config.lastUpdated[routeId];
        saveFilterConfig(config);
        set({ [key]: {} });
        return;
      }

      // Initialize with empty object and current timestamp
      if (!hasFilterData(key)) {
        updateFilterTimestamp(routeId);
        saveFilterData(key, {});
        set({ [key]: {} });
        return;
      }

      set({ [key]: getFilterData(key) });
    },
  }),
  isDeepEqual
);

/**
 * Custom hook to get and update the filter state in the store
 * Only use this hook internally in the filter bar component
 */
export const __useLocalFilterStore = <Schema extends z.ZodType<unknown, z.ZodTypeDef>>(
  routeId: FilterIds['routeId']
): [Partial<z.infer<Schema>>, (value: Partial<z.infer<Schema>> | SetItemCb<Schema>) => void] => {
  const key = useMemo(() => createStorageKey(routeId), [routeId]);

  const state = filterStore((state) => state[key], isDeepEqual) as Partial<z.infer<Schema>>;
  const setItemInStore = filterStore((state) => state.setItem);

  const setItem = useCallback(
    (value: Partial<z.infer<Schema>> | SetItemCb<Schema>) => {
      setItemInStore(routeId, value);
    },
    [routeId, setItemInStore]
  );

  return [state, setItem];
};

/**
 * Track if the filter bar state has been initialized for the current id
 */
const hasInitObject: Record<string, boolean> = {};

/**
 * Initialize the filter bar state for the current id
 * Only use within the main filter bar component
 */
export const __useFilterBarInit = (props: FilterRouteConfig, schema: z.ZodType<unknown, z.ZodTypeDef>) => {
  const initRef = useRef(() => {
    if (hasInitObject?.[props.routeId] === true) {
      return;
    }

    hasInitObject[props.routeId] = true;

    const { getItem, setItem, initRoute } = filterStore.getState();
    const mode = props.routeId.startsWith('key:') ? 'storageKey' : 'routeId';

    let saveToLocalStorage = false;
    if (mode === 'routeId') {
      saveToLocalStorage = true;
    }
    if (props?.options?.restoreOnMount === true) {
      saveToLocalStorage = true;
    }
    initRoute(props.routeId, saveToLocalStorage);

    if (mode === 'storageKey') {
      if (!saveToLocalStorage) {
        setItem(props.routeId, {});
        return;
      }
    }

    if (props?.options?.restoreOnMount === true && mode === 'routeId') {
      const value = getItem(props.routeId) || {};

      const precedence = props?.options?.urlParamsPrecedence || 'url';

      // Use requestAnimationFrame to batch this update with React's rendering
      // This is important to avoid flickering when the page loads
      requestAnimationFrame(() => {
        router.navigate({
          // @ts-ignore
          search: (prev) => {
            if (precedence === 'url') {
              const currentUrlData = schema.safeParse(prev)?.data || {};

              if (Object.keys(currentUrlData).length > 0) {
                setItem(props.routeId, cleanEmptyParams({ ...prev }));
                return prev;
              }

              return cleanEmptyParams({ ...value });
            }

            if (precedence === 'local') {
              return cleanEmptyParams({ ...value });
            }

            // precedence is 'merge'
            return cleanEmptyParams({
              ...prev,
              ...value,
            });
          },
          replace: true,
        });
      });

      return;
    }
  });

  useRef(initRef.current());

  useEffect(() => {
    return () => {
      if (Object.prototype.hasOwnProperty.call(hasInitObject, props.routeId)) {
        delete hasInitObject[props.routeId];
      }
    };
  }, []);
};
